import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Copy, Share2 } from "lucide-react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import BDChat from "@/components/bd/BDChat";
import MinisiteEditor from "@/components/minisite/MinisiteEditor";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { STEP_KEYS, STEP_LABELS, stepTone } from "@/steps";

import FormulairePanel from "./FormulairePanel";
import InterviewPanel from "./InterviewPanel";
import RecitPanel from "./RecitPanel";
import ShareDialog from "./ShareDialog";
import StepDots from "./StepDots";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TONE_CLASSES = {
  done: "bg-green-600",
  current: "bg-primary",
  todo: "border border-muted-foreground/40 bg-muted",
  error: "bg-destructive",
};

export function resolveTab(tab, steps, access) {
  const accessible = (key) => (
    (key !== "bd" || access.bd)
    && (key !== "minisite" || access.minisite)
  );
  const target = STEP_KEYS.includes(tab)
    ? tab
    : steps?.current === "termine"
      ? "minisite"
      : steps?.current;

  if (!STEP_KEYS.includes(target)) return null;
  return accessible(target) ? target : "recit";
}

export default function ClientPage({ canAccessBD, canAccessMinisite, token, userEmail }) {
  const { clientId: routeClientId, tab } = useParams();
  const clientId = routeClientId || "nouveau";
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(clientId !== "nouveau");
  const [lienARecopier, setLienARecopier] = useState(null);

  const loadProgress = useCallback(async (targetClientId = clientId) => {
    if (targetClientId === "nouveau") return null;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/clients/${targetClientId}/progress`);
      setClient(res.data);
      return res.data;
    } catch (error) {
      if ([403, 404].includes(error.response?.status)) {
        toast.error("Dossier introuvable ou inaccessible");
        navigate("/");
      } else {
        toast.error("Impossible de charger le dossier");
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [clientId, navigate]);

  const lastTabRef = useRef(null);
  useEffect(() => {
    lastTabRef.current = null;
    if (clientId === "nouveau") {
      setClient(null);
      setLoading(false);
      return;
    }
    setClient(null);
    loadProgress();
  }, [clientId, loadProgress]);

  // Changement d'onglet : on rafraîchit l'avancement sans écran de chargement
  // (une action dans un onglet — extraction, validation BD, publication — change les étapes).
  useEffect(() => {
    if (clientId === "nouveau" || !client) return;
    const shown = resolveTab(tab, client.steps, { bd: canAccessBD, minisite: canAccessMinisite });
    if (tab !== shown) return; // redirection en cours : pas un changement d'onglet
    if (lastTabRef.current === null || lastTabRef.current === tab) {
      lastTabRef.current = tab; // premier onglet affiché (ou même onglet)
      return;
    }
    lastTabRef.current = tab;
    loadProgress();
  }, [tab, clientId, client, canAccessBD, canAccessMinisite, loadProgress]);

  const handleUploaded = async (uploadedSessionId) => {
    await loadProgress(uploadedSessionId);
    navigate(`/clients/${uploadedSessionId}/recit`);
  };

  const handleCopyFormLink = async () => {
    let url;
    try {
      const res = await axios.get(`${API}/formulaire/dossiers/${clientId}`);
      url = `${window.location.origin}${res.data.form_path}`;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible de récupérer le lien");
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Lien du formulaire copié");
    } catch (error) {
      setLienARecopier(url);
    }
  };

  if (loading && !client) {
    return <p className="p-8 text-center text-muted-foreground">Chargement du dossier...</p>;
  }

  const isNew = clientId === "nouveau";
  const steps = client?.steps;
  const activeTab = tab;
  const resolvedTab = resolveTab(tab, steps, {
    bd: canAccessBD,
    minisite: canAccessMinisite,
  });
  const formLinkIsAvailable = steps?.formulaire?.state === "lien_envoye";

  let content = null;
  if (isNew) {
    content = activeTab === "interview" ? (
      <InterviewPanel sessionId="nouveau" onUploaded={handleUploaded} />
    ) : (
      <Navigate to="/clients/nouveau/interview" replace />
    );
  } else if (!resolvedTab) {
    content = <p className="p-8 text-center text-muted-foreground">avancement indisponible</p>;
  } else if (tab !== resolvedTab) {
    content = <Navigate to={`/clients/${clientId}/${resolvedTab}`} replace />;
  } else if (tab === "formulaire") {
    content = <FormulairePanel sessionId={clientId} steps={steps} />;
  } else if (tab === "interview") {
    content = (
      <InterviewPanel sessionId={clientId} steps={steps} onUploaded={handleUploaded} />
    );
  } else if (tab === "recit") {
    content = (
      <RecitPanel
        sessionId={clientId}
        onNavigateInterview={() => navigate(`/clients/${clientId}/interview`)}
      />
    );
  } else if (tab === "bd") {
    content = <BDChat token={token} />;
  } else if (tab === "minisite") {
    content = <MinisiteEditor />;
  }

  const tabIsDisabled = (key) => (
    (isNew && key !== "interview")
    || (key === "bd" && !canAccessBD)
    || (key === "minisite" && !canAccessMinisite)
  );

  const tabTitle = (key) => {
    if (key === "bd" && !canAccessBD) return "Réservé à l'équipe BD";
    if (key === "minisite" && !canAccessMinisite) return "Réservé à l'équipe Mini-site";
    return undefined;
  };

  return (
    <div className="bg-background">
      <section className="border-b bg-card">
        <div className="mx-auto flex max-w-3xl flex-col gap-4 px-6 py-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">
              {isNew ? "Nouveau dossier" : client?.prenom_nom || "Client sans nom"}
            </h1>
            {!isNew && client?.activite && (
              <p className="text-muted-foreground">{client.activite}</p>
            )}
            {!isNew && client?.tarif && <p className="text-sm">Formule {client.tarif} €</p>}
            {!isNew && <StepDots size="md" steps={steps} />}
          </div>

          {!isNew && (
            <div className="flex flex-wrap gap-2">
              <ShareDialog sessionId={clientId} userEmail={userEmail}>
                <Button variant="outline">
                  <Share2 className="h-4 w-4" />
                  Partager
                </Button>
              </ShareDialog>
              {formLinkIsAvailable && (
                <Button variant="outline" onClick={handleCopyFormLink}>
                  <Copy className="h-4 w-4" />
                  Copier le lien du formulaire
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      <Tabs value={activeTab || ""}>
        <div className="overflow-x-auto border-b">
          <TabsList className="mx-auto flex h-auto max-w-3xl justify-start rounded-none bg-transparent px-6 py-2">
            {STEP_KEYS.map((key) => {
              const tone = stepTone(key, steps?.[key], steps?.current);
              const disabled = tabIsDisabled(key);
              const label = (
                <>
                  <span className={`h-2 w-2 rounded-full ${TONE_CLASSES[tone]}`} />
                  {STEP_LABELS[key]}
                </>
              );
              if (disabled) {
                return (
                  <TabsTrigger
                    key={key}
                    value={key}
                    disabled
                    aria-disabled="true"
                    title={tabTitle(key)}
                    data-testid={`tab-${key}`}
                    className="text-muted-foreground opacity-50"
                  >
                    {label}
                  </TabsTrigger>
                );
              }
              return (
                <TabsTrigger
                  key={key}
                  value={key}
                  asChild
                  title={tabTitle(key)}
                  data-testid={`tab-${key}`}
                >
                  <Link to={`/clients/${clientId}/${key}`}>
                    {label}
                  </Link>
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>
      </Tabs>

      {content}

      <Dialog open={Boolean(lienARecopier)} onOpenChange={(open) => !open && setLienARecopier(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Lien du formulaire</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            La copie automatique a été refusée par le navigateur. Sélectionne le lien et copie-le à la main.
          </p>
          <Input readOnly value={lienARecopier || ""} onFocus={(event) => event.target.select()} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
